import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// 獲取設備列表的異步 thunk
export const fetchDeviceList = createAsyncThunk(
  "/api/setting/fetchDeviceList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch("/api/device/list", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.error === 1) {
        throw new Error("API returned error");
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// 更新設備列表的異步 thunk
export const updateDeviceList = createAsyncThunk(
  "/api/setting/updateDeviceList",
  async ({ list, mode }, { rejectWithValue }) => {
    try {
      const response = await fetch("/api/device/list", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          [mode === 1 ? "power_list" : "water_list"]: list,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.error === 1) {
        throw new Error("API returned error");
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const settingSlice = createSlice({
  name: "setting",
  initialState: {
    // 設備列表數據
    powerList: [],
    waterList: [],
    powerCategory: [],
    waterCategory: [],

    // 載入狀態
    isLoading: false,
    isUpdating: false,

    // 錯誤狀態
    error: null,
    updateError: null,

    // 其他設置
    currentMode: 1, // 默認模式
  },
  reducers: {
    // 清除錯誤
    clearError: (state) => {
      state.error = null;
      state.updateError = null;
    },

    // 設置當前模式
    setCurrentMode: (state, action) => {
      state.currentMode = action.payload;
    },

    // 更新本地的電源列表（用於 UI 即時更新）
    updateLocalPowerList: (state, action) => {
      state.powerList = action.payload;
    },

    // 更新本地的水源列表（用於 UI 即時更新）
    updateLocalWaterList: (state, action) => {
      state.waterList = action.payload;
    },

    // 重置狀態
    resetState: (state) => {
      state.powerList = [];
      state.waterList = [];
      state.powerCategory = [];
      state.waterCategory = [];
      state.isLoading = false;
      state.isUpdating = false;
      state.error = null;
      state.updateError = null;
      state.currentMode = 1;
    },
  },
  extraReducers: (builder) => {
    // 獲取設備列表
    builder
      .addCase(fetchDeviceList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDeviceList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.powerList = action.payload.power_list || [];
        state.waterList = action.payload.water_list || [];
        state.powerCategory = action.payload.power_category || [];
        state.waterCategory = action.payload.water_category || [];
        state.error = null;
      })
      .addCase(fetchDeviceList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // 更新設備列表
      .addCase(updateDeviceList.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(updateDeviceList.fulfilled, (state, action) => {
        state.isUpdating = false;
        // 更新成功後，可以選擇更新本地狀態或重新獲取數據
        if (action.payload.power_list) {
          state.powerList = action.payload.power_list;
        }
        if (action.payload.water_list) {
          state.waterList = action.payload.water_list;
        }
        if (action.payload.power_category) {
          state.powerCategory = action.payload.power_category;
        }
        if (action.payload.water_category) {
          state.waterCategory = action.payload.water_category;
        }
        state.updateError = null;
      })
      .addCase(updateDeviceList.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.payload;
      });
  },
});

// 導出 actions
export const {
  clearError,
  setCurrentMode,
  updateLocalPowerList,
  updateLocalWaterList,
  resetState,
} = settingSlice.actions;

// Selectors
export const selectIsLoading = (state) => state.setting.isLoading;
export const selectIsUpdating = (state) => state.setting.isUpdating;
export const selectError = (state) => state.setting.error;
export const selectUpdateError = (state) => state.setting.updateError;
export const selectCurrentMode = (state) => state.setting.currentMode;

// 組合 selector：獲取當前列表（根據模式）
export const selectCurrentList = (state) => {
  const mode = state.setting.currentMode;
  return mode === 1 ? state.setting.powerList : state.setting.waterList;
};
// 組合 selector：獲取當前列表（根據模式）
export const selectCurrentCategory = (state) => {
  const mode = state.setting.currentMode;
  return mode === 1 ? state.setting.powerCategory : state.setting.waterCategory;
};

// 導出 reducer
export default settingSlice.reducer;
